import React from "react";

import { ConfigureIcon as Configure, MoreIcon as More } from "@packages/ds";
import { FormattedMessage, useIntl } from "react-intl";

import config from "console_config";
import * as Dropdown from "ds/Dropdown";
import useDecodedParams from "Hooks/useDecodedParams";
import getUrl from "Libs/getUrl";
import { deploymentSizingApiEnabledSelector } from "Reducers/environment/deployment";
import { selectFeatureProfilingEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { useAutoscalingConfigLink } from "src/organization/pages/project/environment/ConfigureAutoscaling/useAutoscalingConfigLink";
import ShareLink from "src/organization/pages/project/environment/metrics/components/ShareLink";
import { useAppSelector } from "Store/hooks";

import * as S from "./Service.styles";
import { MetricsDeploymentService } from "./type";
import { useAutoscallingValues } from "./util";

type ServiceActionProps = {
  service: MetricsDeploymentService;
  actions?: React.ReactNode;
  onToggle?: () => void;
  isOpen?: boolean;
};

const ServiceAction = ({
  service,
  onToggle,
  isOpen,
  actions
}: ServiceActionProps) => {
  const { organizationId, projectId, environmentId } = useDecodedParams();
  const intl = useIntl();

  const isProfilingEnabled = useAppSelector(selectFeatureProfilingEnabled);
  const isSizingApiEnabled = useAppSelector(state =>
    deploymentSizingApiEnabledSelector(state, {
      organizationId,
      projectId,
      environmentId
    })
  );
  const { canManageAutoscaling, hasCapability } = useAutoscallingValues(
    service.name
  );

  const shouldShowProfiling =
    isProfilingEnabled &&
    service &&
    ("worker" in service || "web" in service) &&
    config.CUSTOM_BLACKFIRE_CARD_LANGUAGE?.some(
      type => service?.type?.toLowerCase().indexOf(type) !== -1
    );

  const resourceConfigUrl = getUrl({
    key: "organization.project.environment.configureResources",
    props: {
      organizationId,
      projectId,
      environmentId: environmentId || "",
      location: location.pathname + location.search,
      queryParams: ""
    }
  });

  const profilingPath = `/${organizationId}/${projectId}/${environmentId}/profiling?app=${service.name}`;

  const { autoscalingEnabledFlag, handleConfigureAutoscaling } =
    useAutoscalingConfigLink(service);

  const shareLink = window.location.href;

  return (
    <S.Actions>
      {isSizingApiEnabled && (
        <S.configureResources to={resourceConfigUrl}>
          <Configure color="var(--mode-vector-neutral-default)" />
        </S.configureResources>
      )}

      {actions}

      <Dropdown.Root id="services">
        <S.MoreAction aria-label="more-action">
          <More />
        </S.MoreAction>
        <Dropdown.Portal>
          <Dropdown.Content align="end">
            {service.kind === "webapp" &&
              canManageAutoscaling &&
              hasCapability &&
              autoscalingEnabledFlag && (
                <Dropdown.Item onClick={handleConfigureAutoscaling}>
                  {intl.formatMessage({
                    id: "configure.thresholds"
                  })}
                </Dropdown.Item>
              )}
            {shouldShowProfiling && (
              <Dropdown.LinkItem to={profilingPath}>
                <FormattedMessage id="view_profiling" />
              </Dropdown.LinkItem>
            )}
            <Dropdown.Item>
              <ShareLink link={shareLink} />
            </Dropdown.Item>
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>

      {onToggle && (
        <S.ShowMetrics
          onClick={onToggle}
          color="var(--mode-vector-neutral-default)"
          $isOpen={!!isOpen}
        />
      )}
    </S.Actions>
  );
};

export default ServiceAction;
